
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, CardContent, FormHelperText, Grid, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { NOSPACE_REGEX, REMOVEHTML, REMOVENBSP } from "../../utils/constants";
import errorHandler from "../../utils/errorHandler";
import EdiorJodit from "../../components/editor/editorJodit";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { emailServices } from "../../services/AllServices";
import { MESSAGE } from "../../utils/validationMessage";

const editorContentValidation = (cmsData, context) => {
  if (cmsData === undefined || cmsData === null) {
    return context.createError({ message: "Body is required" });
  }

  const plainText = cmsData
    .replace(/<[^>]*>/g, "") 
    .replace(/&nbsp;/gi, " ")
    .trim();

  if (!plainText) {
    return context.createError({ message: "Body is required" });
  }

  if (/^\s|^(&nbsp;)+/i.test(cmsData.replace(/<[^>]*>/g, ""))) {
    return context.createError({ message: "Body should not start with a space" });
  }

  if (plainText.length < 10) {
    return context.createError({ message: "Body must be at least 10 characters long" });
  }

  return true;
};

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Please enter at least 2 characters")
    .max(100, "Title must not be more than 100 characters."),

    body: Yup.string().test(
    "custom-description-validation",
    "Body is required",
    function (value) {
      return editorContentValidation(value, this);
    }),
  subject: Yup.string()
    .required("Subject is required")
    .matches(NOSPACE_REGEX, MESSAGE.NO_SPACE)
    .min(2, "Please enter at least 2 characters")
    .max(100, "Subject must not be more than 100 characters."),
});

export default function AddEditEmailTemplate() {
  const navigate = useNavigate();
  const [cmsData, setCmsData] = useState("");
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      emailServices
        .getByIdEmail({ id: id })
        .then((res) => {
          const data = res?.data?.data;
          formik.setValues({
            title: data.title,
            subject: data.subject,
            body: data.body,
            status: data.status,
          });
          setCmsData(data.body);
        })
        .catch((err) => {
          errorHandler(err);
        });
    }
  }, [id]);

  const formik = useFormik({
    initialValues: { title: "", subject: "", body: "", status: "active" },
    validationSchema,

    onSubmit: (values, { setSubmitting }) => {
      const apiCall = id
        ? emailServices.editEmail({
          id: id,
          title: values.title,
          subject: values.subject,
          body: values.body, // Use Formik's `body`
          status: values.status,
        })
        : emailServices.addEmail({
          title: values.title,
          subject: values.subject,
          body: values.body, // Use Formik's `body`
          status: values.status,
        });

      apiCall
        .then((res) => {
          toast.success(res?.data?.message);
          navigate("/email-template");
          setSubmitting(false);
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },

  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;


  return (
    <>
      <Card>
        <CardContent>
          <FormikProvider value={formik} style={{ padding: "34px 30px" }}>
            <Typography variant="h4" sx={{ mb: 2 }} align="start">
              {id ? "Edit" : "Add"} Email Template
            </Typography>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  name="title"
                  label="Title"
                  {...getFieldProps("title")}
                  error={Boolean(touched.title && errors.title)}
                  helperText={touched.title && errors.title}
                />
                <TextField
                  name="Subject"
                  label="Subject"
                  {...getFieldProps("subject")}
                  error={Boolean(touched.subject && errors.subject)}
                  helperText={touched.subject && errors.subject}
                />
                {/* <EdiorJodit
                  data={cmsData}
                  placeholder="Message"
                  onChange={(html) => {
                    formik.setFieldTouched("body", true);
                    const withoutHtml = html.replace(REMOVEHTML, "").replace(REMOVENBSP, "").trim();

                    formik.setFieldValue("body", withoutHtml ? html : "");
                    setCmsData(html);
                  }}
                />

                {formik.touched.body && formik.errors.body && (
                  <p style={{ color: "red", fontSize: "14px" }}>
                    {formik.errors.body}
                  </p>
                )} */}

                <EdiorJodit
                  formik={formik}
                  fieldName="body"
                  onChange={(val) => formik.setFieldValue("body", val)}
                  data={formik?.values?.body}
                />
                {formik.touched.body && formik.errors.body && (
                  <FormHelperText error>
                    {formik.errors.body}
                  </FormHelperText>
                )}

                <Grid item xs={12}>
                  <Select
                    labelId="status-label"
                    id="status"
                    value={formik.values.status}
                    onChange={(e) =>
                      formik.setFieldValue("status", e.target.value)
                    }
                    fullWidth
                    error={Boolean(
                      formik.touched.status && formik.errors.status
                    )}
                    displayEmpty
                    defaultValue=""
                  >
                    <MenuItem value={"active"}>Active</MenuItem>
                    <MenuItem value={"inactive"}>Inactive</MenuItem>
                  </Select>
                  {formik.touched.status && formik.errors.status && (
                    <FormHelperText error>
                      {formik.errors.status}
                    </FormHelperText>
                  )}
                </Grid>
              </Stack>
              <Stack
                spacing={2}
                mt={3}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Button
                  size="large"
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{ my: 2 }}
                >
                  {id ? "Update" : "Add"}
                </Button>
                <Button
                  size="large"
                  variant="contained"
                  onClick={() => {
                    if (!isSubmitting) navigate("/email-template");
                  }}
                  sx={{ my: 2, ml: 4 }}
                >
                  Cancel
                </Button>
              </Stack>
            </Form>
          </FormikProvider>
        </CardContent>
      </Card>
    </>
  );
}
